// import API
import { mutations, queryKeys } from 'RestAPIs/rapini';

// import components
import ControlledValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';
import { ControlledTimePeriods, showSnackbar } from 'components/shared';
import DutyCodeBaseFields from 'components/shared/DutyCodesGrid/DutyCodeBaseFields';
import {
  defaultFormValues,
  DutyCodeForm
} from 'components/shared/DutyCodesGrid/AddDutyCode/components/AddDutyCode.types';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { DutyCodeGeneralFields } from './GeneralDutyCodeFields';
import { LoadingButton } from '@mui/lab';
import Box from '@mui/material/Box';

// import external libraries
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

// import hooks
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

// import types
import { DutyCodeFlag } from 'generated/index';

// import utils
import getAddEditDutyCodeSchema from 'components/shared/DutyCodesGrid/AddDutyCode/utils/addDutyCodeSchema';
import { convertFlagsObjectToArray } from 'utils/misc';

const AddGeneralDutyCodeForm = ({ handleClose }) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  // mutation for creating a duty code itself
  const { mutate: createDutyCode, isLoading } = mutations.useCreateDutyCode();

  const methods = useForm<DutyCodeForm>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(getAddEditDutyCodeSchema())
  });

  const { control, watch, handleSubmit } = methods;

  const onSubmit = (data: DutyCodeForm) => {
    const dutyCodePayload = {
      validFrom: data.validFrom.toISOString(),
      validTo: data.validTo.toISOString(),
      code: data.displayDutyCode,
      colorCode: data.colorCode,
      description: data.description,
      flags: convertFlagsObjectToArray(data.flags) as DutyCodeFlag[],
      periods: data.periods.map((period) => ({
        endTime: period.endTime.toISOString(),
        startTime: period.startTime.toISOString(),
        dutyCodePeriodId: period.dutyCodePeriodId ?? undefined,
        dutyHourTypeId: period.dutyHourTypeId
      }))
    };

    createDutyCode(dutyCodePayload, {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.getDutyCodes.name);
        handleClose();
      },
      onError: (error: any) => {
        const message = t(
          error?.response?.data?.error?.message || 'somethingWentWrong'
        );
        showSnackbar({
          severity: 'error',
          message: message
        });
      }
    });
  };

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <Box>
        <MultiStepForm.Section
          title={t('basis')}
          validateFields={['displayDutyCode', 'validFrom', 'validTo']}
        >
          <DutyCodeBaseFields />
          <DutyCodeGeneralFields />
          <ControlledValidFromValidTo
            control={control}
            validFromFieldName="validFrom"
            validToFieldName="validTo"
          />
        </MultiStepForm.Section>
        <MultiStepForm.Section
          title={t('periods')}
          validateFields={['periods']}
        >
          <ControlledTimePeriods
            control={control}
            watch={watch}
            hasDHTSelector
            hideBreakDuration
          />
        </MultiStepForm.Section>
      </Box>
      <MultiStepForm.Actions>
        <LoadingButton
          type="submit"
          variant="contained"
          autoFocus
          color="primary"
          loading={isLoading}
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default AddGeneralDutyCodeForm;
